// ============================================================
// Барабан слот-машины (вынесен из tools-random.ts) — переиспользуется
// «Менеджером матча». Внешний контейнер должен задать --rr-item-h
// (высота строки, по умолчанию 68px в tools.css) и иметь класс-предка
// с rr-* стилями. Стили: web/src/styles/tools.css.
// ============================================================

/** Высота строки барабана — должна совпадать с --rr-item-h в tools.css. */
export const REEL_ITEM_H = 68;

export interface ReelItem {
    id: number;
    name: string;
    /** Доп. подсказка (альт. название карты), показывается как title. */
    title?: string | undefined;
}

export interface Reel {
    el: HTMLElement;
    /** Прокрутить барабан; itemH — длительность анимации. */
    spin: (ms: number) => Promise<void>;
    /** Перерисовать статичное состояние (после смены пула). */
    refresh: () => void;
    /** Восстановить выбранный вариант по id (null — сбросить). */
    setSelected: (id: number | null) => void;
    /** Текущий выпавший/выбранный вариант или null. */
    getSelected: () => ReelItem | null;
}

export function buildReel(
    getItems: () => ReelItem[],
    label: string,
    emptyText: string
): Reel {
    const el = document.createElement('div');
    el.className = 'rr-reel';

    const lab = document.createElement('div');
    lab.className = 'rr-reel-label';
    lab.textContent = label;

    const win = document.createElement('div');
    win.className = 'rr-window';
    const strip = document.createElement('div');
    strip.className = 'rr-strip';
    const pointer = document.createElement('div');
    pointer.className = 'rr-pointer';
    win.append(strip, pointer);
    el.append(lab, win);

    let selected: ReelItem | null = null;

    const cell = (text: string, title?: string): HTMLElement => {
        const d = document.createElement('div');
        d.className = 'rr-cell';
        // Текст во внутреннем span — по его ширине подгоняем размер шрифта
        const span = document.createElement('span');
        span.className = 'rr-cell-text';
        span.textContent = text;
        d.appendChild(span);
        if (title) d.title = title;
        return d;
    };

    /** Уменьшает шрифт ячеек, чьё название не влезает в ширину барабана. */
    const fitCells = (cells: Iterable<Element>): void => {
        for (const c of cells) {
            const d = c as HTMLElement;
            const span = d.firstElementChild as HTMLElement | null;
            if (!span) continue;
            d.style.fontSize = '';
            const cs = getComputedStyle(d);
            const avail = d.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
            const need = span.scrollWidth;
            if (avail <= 0 || need <= avail) continue;
            // Не мельче 55% от базового — дальше уже обрезка многоточием
            const k = Math.max(0.55, avail / need);
            d.style.fontSize = `${parseFloat(cs.fontSize) * k}px`;
        }
    };
    // Ширина окна могла измениться (ресайз) — перегоняем шрифты
    new ResizeObserver(() => fitCells(strip.children)).observe(win);

    const randomOf = (arr: ReelItem[]): ReelItem => arr[Math.floor(Math.random() * arr.length)]!;

    /** Статичное состояние: в центре последний результат или «?». */
    const refresh = (): void => {
        const items = getItems();
        strip.style.transition = 'none';
        strip.style.transform = 'translateY(0)';
        strip.innerHTML = '';
        win.classList.toggle('is-empty', items.length === 0);
        win.classList.remove('is-hit');
        if (selected && !items.some((it) => it.id === selected!.id)) selected = null;
        const centerText = items.length === 0 ? emptyText : selected ? selected.name : '?';
        const side = () => (items.length ? randomOf(items).name : '');
        strip.append(cell(side()), cell(centerText, selected?.title), cell(side()));
        if (selected) strip.children[1]?.classList.add('is-hit');
        fitCells(strip.children);
    };

    const spin = (ms: number): Promise<void> => {
        const items = getItems();
        if (items.length === 0) {
            selected = null;
            refresh();
            return Promise.resolve();
        }

        // Цель: случайный вариант, не повторяющий прошлый (если есть из чего выбрать)
        const candidates = items.length > 1 ? items.filter((it) => it.id !== selected?.id) : items;
        const target = randomOf(candidates);
        selected = target;

        // Лента: [сосед, текущий центр, ...наполнитель..., цель, сосед].
        const prevCenter = strip.children[1]?.textContent ?? '?';
        const fillers = Math.round(ms / 70);
        strip.style.transition = 'none';
        strip.style.transform = 'translateY(0)';
        strip.innerHTML = '';
        win.classList.remove('is-hit');
        strip.append(cell(randomOf(items).name), cell(prevCenter));
        for (let i = 0; i < fillers; i++) strip.appendChild(cell(randomOf(items).name));
        const targetCell = cell(target.name, target.title);
        strip.append(targetCell, cell(randomOf(items).name));
        const targetIdx = fillers + 2;
        fitCells(strip.children);

        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const dur = reduced ? 300 : ms;

        win.classList.add('is-spinning');
        void strip.offsetHeight; // reflow, чтобы transition стартовал с 0
        strip.style.transition = `transform ${dur}ms cubic-bezier(0.12, 0.65, 0.12, 1)`;
        strip.style.transform = `translateY(${-(targetIdx - 1) * REEL_ITEM_H}px)`;

        return new Promise((resolve) => {
            setTimeout(() => {
                win.classList.remove('is-spinning');
                win.classList.add('is-hit');
                targetCell.classList.add('is-hit');
                fitCells([targetCell]);
                resolve();
            }, dur + 30);
        });
    };

    /** Ставит выбранный вариант по id (используется при восстановлении состояния). */
    const setSelected = (id: number | null): void => {
        const items = getItems();
        selected = id === null ? null : items.find((it) => it.id === id) ?? null;
        refresh();
    };

    refresh();
    return { el, spin, refresh, setSelected, getSelected: () => selected };
}
